import { buildSrcSet, defaultSizes, isOptimizable, optimizedImageUrl } from "@/lib/image-url";

/**
 * Make article HTML cheap to render and modern-format by default:
 * - every remote <img> src is rewritten to a WebP, CDN-resized URL
 * - a responsive srcset/sizes pair is injected so phones download small files
 * - every <img> after the first gets loading="lazy" + decoding="async"
 * - the first <img> stays eager (it can be the LCP element)
 * - <iframe> embeds (video/maps) always lazy-load
 * Attributes already present in the source markup are never overwritten.
 */
export function lazifyHtml(html: string): string {
  if (!html) return html;

  let imgIndex = 0;
  let out = html.replace(/<img\b([^>]*)>/gi, (_match, attrs: string) => {
    const isFirst = imgIndex++ === 0;
    let next = attrs;

    const srcMatch = next.match(/\bsrc\s*=\s*["']([^"']+)["']/i);
    const src = srcMatch?.[1];
    const widthAttr = Number(next.match(/\bwidth\s*=\s*["']?(\d+)/i)?.[1]) || undefined;

    if (src && isOptimizable(src)) {
      const optimized = optimizedImageUrl(src, widthAttr);
      if (optimized) next = next.replace(srcMatch![0], `src="${optimized}"`);
      if (!/\bsrcset\s*=/i.test(next)) {
        const srcSet = buildSrcSet(src, widthAttr);
        if (srcSet) next += ` srcset="${srcSet}"`;
      }
      if (!/\bsizes\s*=/i.test(next)) next += ` sizes="${defaultSizes(widthAttr)}"`;
    }

    if (!/\bloading\s*=/i.test(next)) next += isFirst ? ' loading="eager"' : ' loading="lazy"';
    if (!/\bdecoding\s*=/i.test(next)) next += ' decoding="async"';
    if (!/\bfetchpriority\s*=/i.test(next)) {
      next += isFirst ? ' fetchpriority="high"' : ' fetchpriority="low"';
    }
    return `<img${next}>`;
  });

  out = out.replace(/<iframe\b([^>]*)>/gi, (match, attrs: string) =>
    /\bloading\s*=/i.test(attrs) ? match : `<iframe${attrs} loading="lazy">`,
  );

  return out;
}
